import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Users, Copy, CheckCircle } from 'lucide-react';

/**
 * Referral program sidebar card: code copy-link + per-level counts + rate
 * table. Controlled via `onCopy` + `copied` from the parent so the parent
 * can share its clipboard timeout state.
 */
export function ReferralProgramCard({ user, referralCfg, referralStats, copied, onCopy }) {
  return (
    <Card className="bg-[#041A3B] border-white/10">
      <CardHeader>
        <CardTitle className="font-outfit text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-[#00E5FF]" />
          Referral Program
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-[#000B18] border border-white/10">
            <p className="text-[#A0B4CC] text-sm mb-2">Your Referral Code</p>
            <div className="flex items-center justify-between">
              <span className="font-mono text-lg text-[#00E5FF]">{user?.referral_code}</span>
              <Button size="sm" variant="ghost" onClick={onCopy} className="text-[#00E5FF]" data-testid="copy-referral-btn">
                {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div
            className={`grid gap-2 text-center grid-cols-${referralCfg.max_levels}`}
            style={{ gridTemplateColumns: `repeat(${referralCfg.max_levels}, minmax(0, 1fr))` }}
          >
            <div className="p-3 rounded-lg bg-[#000B18]">
              <p className="text-xl font-semibold text-white">{referralStats?.level_1_count || 0}</p>
              <p className="text-xs text-[#A0B4CC]">Level 1</p>
            </div>
            {referralCfg.max_levels >= 2 && (
              <div className="p-3 rounded-lg bg-[#000B18]">
                <p className="text-xl font-semibold text-white">{referralStats?.level_2_count || 0}</p>
                <p className="text-xs text-[#A0B4CC]">Level 2</p>
              </div>
            )}
            {referralCfg.max_levels >= 3 && (
              <div className="p-3 rounded-lg bg-[#000B18]">
                <p className="text-xl font-semibold text-white">{referralStats?.level_3_count || 0}</p>
                <p className="text-xs text-[#A0B4CC]">Level 3</p>
              </div>
            )}
          </div>

          <div className="p-4 rounded-lg bg-gradient-to-r from-[#00E5FF]/10 to-[#007AFF]/10 border border-[#00E5FF]/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#A0B4CC] text-sm">Referral Bonus Rates</span>
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-[#A0B4CC]">Level 1</span>
                <span className="text-[#00E5FF]">{referralCfg.level_1_rate}%</span>
              </div>
              {referralCfg.max_levels >= 2 && (
                <div className="flex justify-between">
                  <span className="text-[#A0B4CC]">Level 2</span>
                  <span className="text-[#00E5FF]">{referralCfg.level_2_rate}%</span>
                </div>
              )}
              {referralCfg.max_levels >= 3 && (
                <div className="flex justify-between">
                  <span className="text-[#A0B4CC]">Level 3</span>
                  <span className="text-[#00E5FF]">{referralCfg.level_3_rate}%</span>
                </div>
              )}
            </div>
          </div>

          <Link to="/referrals">
            <Button className="w-full btn-secondary" data-testid="view-referrals-btn">View All Referrals</Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
